<template>
  <div
    :class="[
      embedded ? 'cms-date-input-embedded' : 'cms-form-group',
      { 'cms-form-group-full': fullWidth && !embedded, 'cms-disabled': disabled }
    ]"
  >
    <label v-if="label" :for="inputId" :class="{ 'cms-date-input-sr-only': embedded }">{{ label }}</label>
    <Datepicker
      :id="inputId"
      :model-value="dateValue"
      :format="'dd/MM/yyyy'"
      :enable-time-picker="false"
      :month-change-on-scroll="false"
      :min-date="minDateForPicker"
      :max-date="maxDateForPicker"
      :input-class-name="'cms-input'"
      :disabled="disabled"
      :required="required"
      auto-apply
      class="cms-datepicker"
      @update:model-value="handleDateChange"
    />
    <span v-if="helpText && !embedded" class="cms-form-help">{{ helpText }}</span>
  </div>
</template>

<script lang="ts" setup>
import { computed } from 'vue';
import Datepicker from '@vuepic/vue-datepicker';
import '@vuepic/vue-datepicker/dist/main.css';

interface Props {
  modelValue: string;
  label?: string;
  placeholder?: string;
  disabled?: boolean;
  required?: boolean;
  fullWidth?: boolean;
  /** Omit outer form-group and visible label; label is screen-reader only (for dense layouts). */
  embedded?: boolean;
  helpText?: string;
  /** YYYY-MM-DD — disables dates before this day */
  minDate?: string;
  /** YYYY-MM-DD — disables dates after this day */
  maxDate?: string;
}

const props = withDefaults(defineProps<Props>(), {
  placeholder: 'dd/mm/yyyy',
  disabled: false,
  required: false,
  fullWidth: false,
  embedded: false
});

const emit = defineEmits<{
  (e: 'update:modelValue', value: string): void;
}>();

const inputId = computed(() => `cms-date-input-${Math.random().toString(36).substr(2, 9)}`);

const parseYmdToLocalDate = (value: string | undefined): Date | undefined => {
  if (!value || typeof value !== 'string' || !/^\d{4}-\d{2}-\d{2}$/.test(value)) {
    return undefined;
  }
  const [y, m, d] = value.split('-').map((v) => parseInt(v, 10));
  return new Date(y, m - 1, d);
};

const minDateForPicker = computed(() => parseYmdToLocalDate(props.minDate));
const maxDateForPicker = computed(() => parseYmdToLocalDate(props.maxDate));

// Convert string date (YYYY-MM-DD) to Date object for Datepicker
const dateValue = computed(() => {
  if (!props.modelValue) return null;
  
  if (typeof props.modelValue === 'string' && props.modelValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
    const [year, month, day] = props.modelValue.split('-');
    return new Date(parseInt(year, 10), parseInt(month, 10) - 1, parseInt(day, 10));
  }
  
  // Try to parse as Date
  const date = new Date(props.modelValue);
  if (!isNaN(date.getTime())) {
    return date;
  }
  
  return null;
});

// Convert Date object to YYYY-MM-DD string for storage
const handleDateChange = (date: Date | null) => {
  if (!date) {
    emit('update:modelValue', '');
    return;
  }
  
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  const formattedDate = `${year}-${month}-${day}`;
  emit('update:modelValue', formattedDate);
};
</script>

<style lang="scss" scoped>
.cms-date-input-embedded {
  position: relative;
  margin-bottom: 0;
  min-width: 0;
  width: 100%;
}

.cms-date-input-sr-only {
  position: absolute;
  width: 1px;
  height: 1px;
  padding: 0;
  margin: -1px;
  overflow: hidden;
  clip: rect(0, 0, 0, 0);
  white-space: nowrap;
  border: 0;
}

.cms-datepicker {
  width: 100%;
  
  :deep(.dp__input_wrap) {
    width: 100%;
  }
  
  :deep(.cms-input) {
    width: 100%;
  }

  // Global `.cms-input` uses `padding` shorthand and wins over vue-datepicker’s
  // `.dp__input_icon_pad` (35px for the left calendar icon), so text sits under the icon.
  :deep(.cms-input.dp__input) {
    padding-left: 2.375rem;
  }
}
</style>

